import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { TabView, TabPanel } from 'primereact/tabview';
import { Timeline } from 'primereact/timeline';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { getField } from '../actions/fieldsActions';
import {
    getTemperatureData,
    getPredictionModel,
    getPredictionModelJ2,
    getSolarizationData,
    getSolarizationV2LogEntries,
    getExcelPredictionModel,
    getExcelPredictionJ2Model,
    getExcelSolarizationData,
    getExcelSolarizationV2LogEntries,
    getExcelTemperatureData
} from '../actions/temperatureActions';
import {
    getPathogenLatinLabel,
    PATHOGEN_LETHAL_THERMAL_INTEGRAL,
    PATHOGEN_SOLARIZATION_ORDER,
    isPathogenSolarizationLicensed
} from '../utils/fieldUtils';
import 'chartjs-plugin-gradient';
import {
    FIELD_CHART_MODAL_HEIGHT_PX,
    getChartModalTimelineMarkerColor,
    nematodeSolarLineColorAtDegrees,
    pathogenSolarLineColorAtDegrees,
    solarLogDate,
    solarLogDegrees,
    sortChartLabelsChronologically,
    sortTimelineEventsChronologically,
    buildPathogenSolarTimelineEvents
} from './fieldChartModalHelpers';
import { formatApiDateMinDayStart } from '../utils/functions';

/**
 * Modal a pantalla completa: gráfico + timeline enfocado a una sola serie (mode).
 * Modes: 'ovicide' | 'larvicide' | 'nematode-solar' | 'pathogen' | 'temperature'.
 * Solo se hacen las peticiones necesarias al backend para el modo activo.
 */
export const FieldChartTimelineModal = ({
    visible,
    onHide,
    fieldId,
    initialFieldData = null,
    mode = 'temperature',
    pathogenCode = null
}) => {
    const { t } = useTranslation();
    const [fieldData, setFieldData] = useState(initialFieldData);
    const [initDate, setInitDate] = useState(null);
    const [lastDate, setLastDate] = useState(new Date());
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [chartOptions, setChartOptions] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [showDownloadDataDialog, setShowDownloadDataDialog] = useState(false);
    const [activeMode, setActiveMode] = useState(mode);
    const [activePathogenCode, setActivePathogenCode] = useState(pathogenCode ?? null);

    const chartFetchGenerationRef = useRef(0);
    /** Solo se incrementa al cargar línea de tiempo; un refetch solo de gráfico no invalida el async del timeline. */
    const timelineFetchGenerationRef = useRef(0);
    const modelMenuRef = useRef(null);

    const f = fieldData;
    const isMediumMobile = typeof window !== 'undefined' && window.innerWidth < 425;

    const titleFieldName = (() => {
        const fromF = f?.name;
        if (fromF != null && String(fromF).trim() !== '') return String(fromF).trim();
        if (initialFieldData != null && String(initialFieldData.id) === String(fieldId)) {
            const n = initialFieldData.name;
            if (n != null && String(n).trim() !== '') return String(n).trim();
        }
        return '';
    })();

    useEffect(() => {
        if (!visible) return;
        setActiveMode(mode);
        setActivePathogenCode(pathogenCode ?? null);
    }, [visible, fieldId, mode, pathogenCode]);

    const pathogenCodeUpper = activePathogenCode ? String(activePathogenCode).toUpperCase() : null;
    const isNematode = activeMode === 'ovicide' || activeMode === 'larvicide';
    const isSolar = activeMode === 'nematode-solar' || activeMode === 'pathogen';
    const isTemperature = activeMode === 'temperature';

    const nematodePredictionModeLabel = (mode) =>
        `${t('field.chart_modal_nematode')} ${t(mode === 'ovicide' ? 'field.ovicide' : 'field.larvicide')}`;

    const solarizationModeLabel = (variant) => {
        if (variant === 'nematode-solar') {
            return t('field.chart_modal_solarization_nematodes');
        }
        return t('field.chart_modal_solarization_pathogen', {
            pathogen: getPathogenLatinLabel(variant)
        });
    };

    const headerText = (() => {
        if (activeMode === 'ovicide' || activeMode === 'larvicide') {
            return nematodePredictionModeLabel(activeMode);
        }
        if (activeMode === 'nematode-solar') return solarizationModeLabel('nematode-solar');
        if (activeMode === 'pathogen' && pathogenCodeUpper) return solarizationModeLabel(pathogenCodeUpper);
        return t('field.temperature');
    })();

    const modelSwitcherOptions = useMemo(() => {
        const lic = f?.sensors?.[0]?.license_info;
        if (!lic || !f) return [];
        const out = [];
        if (f.active_prediction_model) {
            if (lic.nematode_ovicide) {
                out.push({ label: nematodePredictionModeLabel('ovicide'), value: 'ovicide' });
            }
            if (lic.nematode_larvicide) {
                out.push({ label: nematodePredictionModeLabel('larvicide'), value: 'larvicide' });
            }
        }
        if (f.active_solarization && lic.solarization) {
            out.push({ label: solarizationModeLabel('nematode-solar'), value: 'nematode-solar' });
            for (const code of PATHOGEN_SOLARIZATION_ORDER) {
                if (!isPathogenSolarizationLicensed(lic, code)) continue;
                const cu = String(code).toUpperCase();
                out.push({ label: solarizationModeLabel(cu), value: `pathogen:${cu}` });
            }
        }
        return out;
    }, [f, t]);

    const applyModelSwitch = useCallback((v) => {
        if (typeof v === 'string' && v.startsWith('pathogen:')) {
            setActiveMode('pathogen');
            setActivePathogenCode(v.slice('pathogen:'.length));
        } else if (v != null) {
            setActiveMode(v);
            setActivePathogenCode(null);
        }
    }, []);

    const modelMenuItems = useMemo(
        () =>
            modelSwitcherOptions.map((opt) => ({
                label: opt.label,
                command: () => {
                    applyModelSwitch(opt.value);
                }
            })),
        [modelSwitcherOptions, applyModelSwitch]
    );

    /**
     * Al abrir el modal: usa `initialFieldData` si el id coincide (evita getField y, con él, el doble
     * disparo de fetchChartData). Solo cae al fetch remoto si no se recibió data inicial.
     */
    useEffect(() => {
        if (!visible) {
            setFieldData(initialFieldData || null);
            return;
        }
        if (initialFieldData != null && String(initialFieldData.id) === String(fieldId)) {
            setFieldData(initialFieldData);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const result = await getField(fieldId);
                if (cancelled || !result) return;
                setFieldData(result);
            } catch (e) {
                console.error('FieldChartTimelineModal getField:', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, fieldId, initialFieldData]);

    useEffect(() => {
        if (!visible || !fieldData) return;
        let newDateInit = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        setEvents([]);
        setChartData({ labels: [], datasets: [] });
        if (isNematode || activeMode === 'nematode-solar' || activeMode === 'pathogen') {
            setTimelineLoading(true);
        } else {
            setTimelineLoading(false);
        }

        if (isNematode && fieldData.active_prediction_model?.started) {
            const startedDate = new Date(fieldData.active_prediction_model.started.replace('Z', ''));
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            newDateInit = startedDate < oneMonthAgo ? oneMonthAgo : startedDate;
        } else if (isSolar && fieldData.active_solarization?.started) {
            const startedDate = new Date(fieldData.active_solarization.started.replace('Z', ''));
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            newDateInit = startedDate < oneMonthAgo ? oneMonthAgo : startedDate;
        } else {
            newDateInit = sevenDaysAgo;
        }

        setInitDate(newDateInit);
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            fetchChartData(newDateInit, lastDate);
        });
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, fieldData, activeMode, pathogenCodeUpper]);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
    };

    const formatDateChart = (date, interval) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hour = String(date.getHours()).padStart(2, '0');
        if (interval === 'H') {
            return `${day}-${month}-${year} ${hour}:00`;
        }
        return `${day}-${month}-${year}`;
    };

    /**
     * Reasigna fechas dentro del array de entradas del modelo (ovicide / larvicide)
     * para que las generaciones más bajas se queden con las fechas más antiguas.
     * Útil cuando la API devuelve gen N+1 con fecha anterior a gen N: se ordenan
     * todas las fechas y se reparten en bloque a cada generación, preservando el
     * orden interno por fecha de cada grupo. Solo toca `date`; el resto de campos
     * (ratios, color, eggs, etc.) se mantienen intactos.
     */
    const reorderModelDataByGeneration = (modelData) => {
        if (!Array.isArray(modelData) || modelData.length === 0) return modelData;
        const withGen = modelData.filter((e) => e.generation != null);
        const withoutGen = modelData.filter((e) => e.generation == null);
        if (withGen.length === 0) return modelData;
        const sortedDates = withGen
            .map((e) => e.date)
            .sort((a, b) => new Date(a) - new Date(b));
        const byGen = withGen.reduce((acc, e) => {
            (acc[e.generation] = acc[e.generation] || []).push(e);
            return acc;
        }, {});
        const sortedGens = Object.keys(byGen)
            .map(Number)
            .sort((a, b) => a - b);
        const reassigned = [];
        let idx = 0;
        sortedGens.forEach((gen) => {
            const entries = byGen[gen]
                .slice()
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            entries.forEach((e) => {
                reassigned.push({ ...e, date: sortedDates[idx++] });
            });
        });
        return [...reassigned, ...withoutGen];
    };

    /** Timeline ovicide a partir de prediction model (huevos). */
    const buildOvicideTimelineEvents = (modelData, modelStartDateStr) => {
        const events = [];
        if (modelStartDateStr) events.push({ status: 'start_model', date: modelStartDateStr });
        const uniqueGens = [...new Set((modelData || []).map((e) => e.generation))];
        uniqueGens.forEach((gen) => {
            const gEntries = (modelData || []).filter((e) => e.generation === gen);
            const warn = gEntries
                .filter((e) => e.ratio_eggs * 100 >= 80 && e.ratio_eggs * 100 < 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (warn.length > 0) {
                events.push({ status: 'nematode_head_warning_eggs', date: warn[0].date, generation: gen });
            }
            const alert = gEntries
                .filter((e) => e.ratio_eggs * 100 >= 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (alert.length > 0) {
                events.push({ status: 'nematode_head_alert_eggs', date: alert[0].date, generation: gen });
            }
        });
        return sortTimelineEventsChronologically(events);
    };

    /** Timeline larvicide a partir de prediction J2. */
    const buildLarvicideTimelineEvents = (modelDataJ2, modelStartDateStr) => {
        const events = [];
        if (modelStartDateStr) events.push({ status: 'start_model', date: modelStartDateStr });
        const uniqueGens = [...new Set((modelDataJ2 || []).map((e) => e.generation))];
        const firstGenInModel = uniqueGens.length ? Math.min(...uniqueGens.map((g) => Number(g))) : null;
        uniqueGens.forEach((gen) => {
            const gEntries = (modelDataJ2 || []).filter((e) => e.generation === gen);
            const warn = gEntries
                .filter((e) => e.ratio_larvicide >= 80 && e.ratio_larvicide < 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            // Sin "preparación" para la primera generación presente (0, 6, …): suele arrancar ya muy desarrollada.
            if (warn.length > 0 && firstGenInModel != null && Number(gen) !== firstGenInModel) {
                events.push({ status: 'nematode_head_warning_larvae', date: warn[0].date, generation: gen });
            }
            const alert = gEntries
                .filter((e) => e.ratio_larvicide >= 100)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            if (alert.length > 0) {
                events.push({ status: 'nematode_head_alert_larvae', date: alert[0].date, generation: gen });
            }
        });
        return sortTimelineEventsChronologically(events);
    };

    /** Timeline nematoda (solarización) con thresholds low/moderate/good/excellent/super. */
    const buildSolarNematodeTimelineEvents = (solarRows, modelStartDateStr) => {
        const start = modelStartDateStr ? [{ status: 'start_model', date: modelStartDateStr }] : [];
        const pick = (fromDeg, toDeg, status) =>
            (solarRows || [])
                .filter((e) => e.degrees >= fromDeg && (toDeg == null || e.degrees < toDeg))
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .slice(0, 1)
                .map((e) => ({ status, date: e.date }));
        const combined = [
            ...start,
            ...pick(50, 500, 'low'),
            ...pick(500, 1500, 'moderate'),
            ...pick(1500, 3000, 'good'),
            ...pick(3000, 6000, 'excellent'),
            ...pick(7000, null, 'super')
        ];
        return sortTimelineEventsChronologically(combined);
    };

    const fetchChartData = async (initD, endD, { includeTimeline = true } = {}) => {
        chartFetchGenerationRef.current += 1;
        const chartGen = chartFetchGenerationRef.current;

        const formattedInitDate = formatApiDateMinDayStart(initD);
        const nextDay = new Date(endD);
        nextDay.setDate(nextDay.getDate() + 1);
        const formattedLastDate = formatDate(nextDay);
        const interval = isNematode ? 'D' : 'H';

        setIsLoading(true);
        const needsTimelineMode =
            activeMode === 'ovicide' || activeMode === 'larvicide' || activeMode === 'nematode-solar' || activeMode === 'pathogen';
        if (includeTimeline && needsTimelineMode) {
            setTimelineLoading(true);
        }
        const tempPromise = getTemperatureData(fieldData.id, interval, formattedInitDate, formattedLastDate);

        try {
            let modelData = null;
            let modelDataJ2 = null;
            let solarRows = null;
            let pathogenRows = null;

            if (activeMode === 'ovicide' && fieldData.active_prediction_model?.id != null) {
                modelData = await getPredictionModel(
                    fieldData.active_prediction_model.id,
                    formattedInitDate,
                    formattedLastDate
                );
                modelData = reorderModelDataByGeneration(modelData);
            } else if (activeMode === 'larvicide' && fieldData.active_prediction_model?.id != null) {
                modelDataJ2 = await getPredictionModelJ2(
                    fieldData.active_prediction_model.id,
                    formattedInitDate,
                    formattedLastDate
                );
                modelDataJ2 = reorderModelDataByGeneration(modelDataJ2);
            } else if (activeMode === 'nematode-solar') {
                solarRows = await getSolarizationData(fieldData.id, formattedInitDate, formattedLastDate);
            } else if (activeMode === 'pathogen' && pathogenCodeUpper) {
                pathogenRows = await getSolarizationV2LogEntries(
                    fieldData.id,
                    formattedInitDate,
                    formattedLastDate,
                    pathogenCodeUpper
                );
                if (!Array.isArray(pathogenRows)) pathogenRows = [];
            }

            const modelStartDateStr = isNematode
                ? fieldData.active_prediction_model?.started
                    ? formatDate(new Date(fieldData.active_prediction_model.started.replace('Z', '')))
                    : formattedInitDate
                : isSolar
                  ? fieldData.active_solarization?.started
                      ? formatDate(new Date(fieldData.active_solarization.started.replace('Z', '')))
                      : formattedInitDate
                  : null;

            if (includeTimeline && (activeMode === 'ovicide' || activeMode === 'larvicide' || activeMode === 'nematode-solar' || activeMode === 'pathogen')) {
                timelineFetchGenerationRef.current += 1;
                const timelineGen = timelineFetchGenerationRef.current;
                setEvents([{ status: 'start_model', date: modelStartDateStr }].filter((e) => e.date));
                void (async () => {
                    try {
                        const startedIso = isNematode
                            ? fieldData.active_prediction_model?.started
                            : fieldData.active_solarization?.started;
                        if (!startedIso) {
                            if (timelineGen === timelineFetchGenerationRef.current) setTimelineLoading(false);
                            return;
                        }
                        const modelStart = new Date(startedIso.replace('Z', ''));
                        let inclusiveEnd = new Date();
                        if (inclusiveEnd.getTime() < modelStart.getTime()) {
                            inclusiveEnd = new Date(modelStart.getTime());
                        }
                        const exclusiveEnd = new Date(inclusiveEnd);
                        exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
                        const rangeTimelineMin = formatApiDateMinDayStart(modelStart);
                        const formattedRangeEnd = formatDate(exclusiveEnd);

                        if (activeMode === 'ovicide') {
                            const predFull = await getPredictionModel(
                                fieldData.active_prediction_model.id,
                                rangeTimelineMin,
                                formattedRangeEnd
                            );
                            if (timelineGen !== timelineFetchGenerationRef.current) return;
                            setEvents(buildOvicideTimelineEvents(predFull || [], modelStartDateStr));
                        } else if (activeMode === 'larvicide') {
                            const predJ2Full = await getPredictionModelJ2(
                                fieldData.active_prediction_model.id,
                                rangeTimelineMin,
                                formattedRangeEnd
                            );
                            if (timelineGen !== timelineFetchGenerationRef.current) return;
                            setEvents(buildLarvicideTimelineEvents(predJ2Full || [], modelStartDateStr));
                        } else if (activeMode === 'nematode-solar') {
                            const solarFull = await getSolarizationData(
                                fieldData.id,
                                rangeTimelineMin,
                                formattedRangeEnd
                            );
                            if (timelineGen !== timelineFetchGenerationRef.current) return;
                            setEvents(buildSolarNematodeTimelineEvents(solarFull || [], modelStartDateStr));
                        } else if (activeMode === 'pathogen' && pathogenCodeUpper) {
                            const pathFull = await getSolarizationV2LogEntries(
                                fieldData.id,
                                rangeTimelineMin,
                                formattedRangeEnd,
                                pathogenCodeUpper
                            );
                            if (timelineGen !== timelineFetchGenerationRef.current) return;
                            setEvents(
                                buildPathogenSolarTimelineEvents(
                                    pathogenCodeUpper,
                                    Array.isArray(pathFull) ? pathFull : [],
                                    modelStartDateStr
                                )
                            );
                        }
                    } catch (err) {
                        console.error('Error cargando timeline:', err);
                    } finally {
                        if (timelineGen === timelineFetchGenerationRef.current) setTimelineLoading(false);
                    }
                })();
            } else if (includeTimeline) {
                setTimelineLoading(false);
            }

            const temperatureData = await tempPromise;

            const labelSet = new Set(
                Object.keys(temperatureData).map((ds) => formatDateChart(new Date(ds), interval))
            );
            const addLabels = (rows, getD) => {
                (rows || []).forEach((e) => {
                    const raw = getD(e);
                    if (raw == null || raw === '') return;
                    const d = new Date(raw);
                    if (Number.isNaN(d.getTime())) return;
                    labelSet.add(formatDateChart(d, interval));
                });
            };
            if (activeMode === 'ovicide') addLabels(modelData, (e) => e?.date);
            if (activeMode === 'larvicide') addLabels(modelDataJ2, (e) => e?.date);
            if (activeMode === 'nematode-solar') addLabels(solarRows, (e) => e?.date);
            if (activeMode === 'pathogen') addLabels(pathogenRows, (e) => solarLogDate(e));

            const labels = sortChartLabelsChronologically([...labelSet], interval);

            const temperatureDataset = labels.map((label) => {
                const key = Object.keys(temperatureData).find(
                    (ds) => formatDateChart(new Date(ds), interval) === label
                );
                return key ? temperatureData[key].mean : null;
            });

            const colorByEggsPct = (value) => {
                if (value == null || !Number.isFinite(Number(value))) return 'transparent';
                const v = Number(value);
                /* Alineado con getModel (fieldUtils) y FieldCard: ≥120 % fase «esperar» (verde); 100–119 rojo; 80–99 naranja. */
                if (v >= 120) return '#7ECA22';
                if (v >= 100) return '#ED5F55';
                if (v >= 80) return '#F6894E';
                return '#7ECA22';
            };
            const colorByJ2Code = (code) => {
                if (code == null) return 'transparent';
                /* Misma lógica visual que FieldCard: API blue → verde esperar. */
                if (code === 'blue') return '#7ECA22';
                if (code === 'green') return '#7ECA22';
                if (code === 'orange') return '#F6894E';
                if (code === 'red') return '#ED5F55';
                return '#7ECA22';
            };

            const datasets = [
                {
                    label: t('field.temperature'),
                    fill: false,
                    borderColor: '#8A99AD',
                    tension: 0.4,
                    data: temperatureDataset,
                    yAxisID: 'y',
                    backgroundColor: 'transparent',
                    pointBackgroundColor: '#8A99AD',
                    pointBorderColor: '#8A99AD',
                    pointRadius: window.innerWidth < 500 ? 0 : 2,
                    pointHoverRadius: window.innerWidth < 500 ? 0 : 5,
                    borderWidth: 3
                }
            ];

            let y1Config = null;

            if (activeMode === 'ovicide' && modelData && modelData.length > 0) {
                const dataset = labels.map((label) => {
                    const e = modelData.find((x) => formatDateChart(new Date(x.date), interval) === label);
                    return e ? e.ratio_eggs * 100 : null;
                });
                const capped = dataset.map((v) => {
                    if (v == null || !Number.isFinite(Number(v))) return null;
                    const n = Number(v);
                    /* ≥120 % = fase API azul: mismo criterio que FieldCard → valor 0 en el gráfico. */
                    if (n >= 120) return 0;
                    if (n > 100) return 100;
                    return n;
                });
                let lastColor = '#7ECA22';
                for (let i = dataset.length - 1; i >= 0; i--) {
                    if (dataset[i] != null) {
                        lastColor = colorByEggsPct(dataset[i]);
                        break;
                    }
                }
                datasets.push({
                    label: t('field.ovicide'),
                    borderColor: lastColor,
                    fill: false,
                    tension: 0.4,
                    data: capped,
                    yAxisID: 'y1',
                    pointBackgroundColor: dataset.map((v) => colorByEggsPct(v)),
                    pointBorderColor: dataset.map((v) => colorByEggsPct(v)),
                    pointRadius: window.innerWidth < 500 ? 0 : 2,
                    pointHoverRadius: window.innerWidth < 500 ? 0 : 5,
                    borderWidth: 3,
                    segment: {
                        borderColor: (ctx) => colorByEggsPct(dataset[ctx.p1DataIndex]),
                        backgroundColor: (ctx) => `${colorByEggsPct(dataset[ctx.p1DataIndex])}33`
                    }
                });
                y1Config = {
                    min: 0,
                    max: 105,
                    stepSize: 5,
                    title: t('field.model_nematode_axis'),
                    callback: (value) => (value <= 100 ? value : '')
                };
            } else if (activeMode === 'larvicide' && modelDataJ2 && modelDataJ2.length > 0) {
                const dataset = labels.map((label) => {
                    const e = modelDataJ2.find((x) => formatDateChart(new Date(x.date), interval) === label);
                    return e ? e.ratio_larvicide : null;
                });
                const colorCodes = labels.map((label) => {
                    const e = modelDataJ2.find((x) => formatDateChart(new Date(x.date), interval) === label);
                    return e ? e.color : null;
                });
                const capped = dataset.map((v, i) => {
                    if (v == null || !Number.isFinite(Number(v))) return null;
                    /* API color blue → igual que FieldCard: 0 % en el gráfico. */
                    if (colorCodes[i] === 'blue') return 0;
                    const n = Number(v);
                    return n > 100 ? 100 : n;
                });
                let lastColor = '#7ECA22';
                for (let i = dataset.length - 1; i >= 0; i--) {
                    if (dataset[i] != null) {
                        lastColor = colorByJ2Code(colorCodes[i]);
                        break;
                    }
                }
                datasets.push({
                    label: t('field.larvicide'),
                    borderColor: lastColor,
                    fill: false,
                    tension: 0.4,
                    data: capped,
                    yAxisID: 'y1',
                    pointBackgroundColor: colorCodes.map((c) => colorByJ2Code(c)),
                    pointBorderColor: colorCodes.map((c) => colorByJ2Code(c)),
                    pointRadius: window.innerWidth < 500 ? 0 : 2,
                    pointHoverRadius: window.innerWidth < 500 ? 0 : 5,
                    borderWidth: 3,
                    segment: {
                        borderColor: (ctx) => colorByJ2Code(colorCodes[ctx.p1DataIndex]),
                        backgroundColor: (ctx) => `${colorByJ2Code(colorCodes[ctx.p1DataIndex])}33`
                    }
                });
                y1Config = {
                    min: 0,
                    max: 105,
                    stepSize: 5,
                    title: t('field.model_nematode_axis'),
                    callback: (value) => (value <= 100 ? value : '')
                };
            } else if (activeMode === 'nematode-solar' && solarRows && solarRows.length > 0) {
                const dataset = labels.map((label) => {
                    const e = solarRows.find((x) => formatDateChart(new Date(x.date), interval) === label);
                    return e ? e.degrees : null;
                });
                const capped = dataset.map((v) => (v != null && v > 7000 ? 7000 : v));
                let lastColor = '#7ECA22';
                for (let i = dataset.length - 1; i >= 0; i--) {
                    if (dataset[i] != null) {
                        lastColor = nematodeSolarLineColorAtDegrees(dataset[i]);
                        break;
                    }
                }
                datasets.push({
                    label: t('field_card.solarization_nematodes_heading'),
                    borderColor: lastColor,
                    fill: false,
                    tension: 0.4,
                    data: capped,
                    yAxisID: 'y1',
                    pointBackgroundColor: dataset.map((v) =>
                        v == null ? 'transparent' : nematodeSolarLineColorAtDegrees(v)
                    ),
                    pointBorderColor: dataset.map((v) =>
                        v == null ? 'transparent' : nematodeSolarLineColorAtDegrees(v)
                    ),
                    pointRadius: window.innerWidth < 500 ? 0 : 2,
                    pointHoverRadius: window.innerWidth < 500 ? 0 : 5,
                    borderWidth: 3,
                    segment: {
                        borderColor: (ctx) => {
                            const v = dataset[ctx.p1DataIndex];
                            return v == null ? 'transparent' : nematodeSolarLineColorAtDegrees(v);
                        },
                        backgroundColor: (ctx) => {
                            const v = dataset[ctx.p1DataIndex];
                            if (v == null) return 'transparent';
                            return `${nematodeSolarLineColorAtDegrees(v)}33`;
                        }
                    }
                });
                y1Config = {
                    min: 0,
                    max: 7700,
                    stepSize: 700,
                    title: t('field.model_solarization_nematode_axis'),
                    callback: (value) => (value <= 7000 ? value : '')
                };
            } else if (activeMode === 'pathogen' && pathogenCodeUpper && pathogenRows && pathogenRows.length > 0) {
                const dataset = labels.map((label) => {
                    const e = pathogenRows.find((x) => {
                        const raw = solarLogDate(x);
                        if (raw == null) return false;
                        return formatDateChart(new Date(raw), interval) === label;
                    });
                    if (e == null) return null;
                    const deg = solarLogDegrees(e);
                    return deg != null ? deg : null;
                });
                const lethalCap = PATHOGEN_LETHAL_THERMAL_INTEGRAL[pathogenCodeUpper] || 1200;
                const maxObs = Math.max(...dataset.filter((v) => v != null).map(Number), 1);
                const yMax = Math.min(6000, Math.ceil(Math.max(maxObs * 1.1, lethalCap) / 50) * 50);
                const capped = dataset.map((v) => (v != null && v > yMax ? yMax : v));
                let lastColor = '#7ECA22';
                for (let i = dataset.length - 1; i >= 0; i--) {
                    if (dataset[i] != null) {
                        lastColor = pathogenSolarLineColorAtDegrees(dataset[i], pathogenCodeUpper);
                        break;
                    }
                }
                datasets.push({
                    label: getPathogenLatinLabel(pathogenCodeUpper),
                    borderColor: lastColor,
                    fill: false,
                    tension: 0.4,
                    data: capped,
                    yAxisID: 'y1',
                    pointBackgroundColor: dataset.map((v) =>
                        v == null ? 'transparent' : pathogenSolarLineColorAtDegrees(v, pathogenCodeUpper)
                    ),
                    pointBorderColor: dataset.map((v) =>
                        v == null ? 'transparent' : pathogenSolarLineColorAtDegrees(v, pathogenCodeUpper)
                    ),
                    pointRadius: window.innerWidth < 500 ? 0 : 2,
                    pointHoverRadius: window.innerWidth < 500 ? 0 : 5,
                    borderWidth: 3,
                    segment: {
                        borderColor: (ctx) => {
                            const v = dataset[ctx.p1DataIndex];
                            return v == null
                                ? 'transparent'
                                : pathogenSolarLineColorAtDegrees(v, pathogenCodeUpper);
                        },
                        backgroundColor: (ctx) => {
                            const v = dataset[ctx.p1DataIndex];
                            if (v == null) return 'transparent';
                            return `${pathogenSolarLineColorAtDegrees(v, pathogenCodeUpper)}33`;
                        }
                    }
                });
                y1Config = {
                    min: 0,
                    max: yMax,
                    title: `${getPathogenLatinLabel(pathogenCodeUpper)} (ºC·d)`
                };
            }

            if (chartGen !== chartFetchGenerationRef.current) return;

            setChartData({ labels, datasets });
            setChartOptions(buildChartOptions(y1Config));
        } catch (err) {
            console.error('FieldChartTimelineModal fetchChartData:', err);
            if (includeTimeline && chartGen === chartFetchGenerationRef.current) {
                setTimelineLoading(false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const buildChartOptions = (y1Config) => {
        const base = {
            animation: { duration: 0 },
            stacked: false,
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        boxWidth: 10,
                        boxHeight: 10,
                        padding: 10,
                        color: '#000000',
                        pointStyle: 'circle',
                        generateLabels: (chart) => {
                            const datasets = chart.data.datasets;
                            return datasets.map((dataset, i) => ({
                                text: dataset.label,
                                fillStyle: dataset.borderColor,
                                strokeStyle: dataset.borderColor,
                                lineWidth: 2,
                                hidden: !chart.isDatasetVisible(i),
                                index: i
                            }));
                        }
                    },
                    onClick: (e, legendItem, legend) => {
                        const index = legendItem.index;
                        const ci = legend.chart;
                        const meta = ci.getDatasetMeta(index);
                        meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;
                        ci.update();
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: '#495057' },
                    grid: { color: '#ebedef' }
                },
                y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    ticks: { color: '#495057' },
                    grid: { color: '#ebedef' },
                    title: {
                        display: true,
                        text: t('field.temperature_axis'),
                        color: '#495057'
                    }
                }
            }
        };

        if (y1Config) {
            base.scales.y1 = {
                beginAtZero: true,
                type: 'linear',
                display: true,
                position: 'right',
                min: y1Config.min,
                max: y1Config.max,
                ticks: {
                    color: '#495057',
                    ...(y1Config.stepSize ? { stepSize: y1Config.stepSize } : {}),
                    ...(y1Config.callback ? { callback: y1Config.callback } : {})
                },
                grid: { drawOnChartArea: false, color: '#ebedef' },
                title: {
                    display: true,
                    text: y1Config.title,
                    color: '#495057'
                }
            };
        }

        return base;
    };

    const downloadData = async (type) => {
        setShowDownloadDataDialog(true);
        // `type` unused because the accept/reject of the ConfirmDialog decides the final download
        void type;
    };

    const downloadModelData = async () => {
        let response;
        const timezoneOffset = (new Date().getTimezoneOffset() * -1) / 60;
        if (activeMode === 'ovicide') {
            response = await getExcelPredictionModel(
                f.id,
                formatApiDateMinDayStart(initDate),
                formatDate(lastDate),
                timezoneOffset
            );
        } else if (activeMode === 'larvicide') {
            response = await getExcelPredictionJ2Model(
                f.id,
                formatApiDateMinDayStart(initDate),
                formatDate(lastDate),
                timezoneOffset
            );
        } else if (activeMode === 'pathogen' && pathogenCodeUpper) {
            response = await getExcelSolarizationV2LogEntries(
                f.id,
                formatApiDateMinDayStart(initDate),
                formatDate(lastDate),
                pathogenCodeUpper,
                timezoneOffset
            );
        } else if (activeMode === 'nematode-solar') {
            response = await getExcelSolarizationData(
                f.id,
                formatApiDateMinDayStart(initDate),
                formatDate(lastDate),
                timezoneOffset
            );
        } else {
            return;
        }
        if (!response) {
            return;
        }
        const url = window.URL.createObjectURL(await response.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download =
            activeMode === 'pathogen' && pathogenCodeUpper
                ? `solarization-v2-${String(pathogenCodeUpper).replace(/[\\/:*?"<>|]/g, '_')}.xlsx`
                : `data-${activeMode}-model.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const downloadTemperatureData = async () => {
        const interval = isNematode ? 'D' : 'H';
        const timezoneOffset = (new Date().getTimezoneOffset() * -1) / 60;
        const response = await getExcelTemperatureData(
            f.id,
            interval,
            formatApiDateMinDayStart(initDate),
            formatDate(lastDate),
            timezoneOffset
        );
        const url = window.URL.createObjectURL(await response.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = 'data-temperature.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleClose = () => onHide();

    if (!visible) {
        return null;
    }

    const renderCalendars = () => (
        <div
            className="flex flex-row flex-nowrap align-items-center justify-content-center gap-2 w-full flex-shrink-0 pt-2 mb-3"
            style={{ maxWidth: '100%' }}
        >
            <div style={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}>
                <Calendar
                    id="field-card-modal-init-date"
                    className="w-full"
                    pt={{
                        root: { className: 'w-full', style: { maxWidth: '100%' } },
                        dropdownButton: { className: 'bg-primary' }
                    }}
                    style={{ height: '30px', width: '100%' }}
                    inputStyle={
                        isMediumMobile
                            ? { fontSize: '13px', padding: '3px', minWidth: 0 }
                            : { minWidth: 0 }
                    }
                    value={initDate}
                    onChange={(e) => {
                        setInitDate(e.value);
                        fetchChartData(e.value, lastDate, { includeTimeline: false });
                    }}
                    showIcon
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                />
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}>
                <Calendar
                    id="field-card-modal-last-date"
                    className="w-full"
                    pt={{
                        root: { className: 'w-full', style: { maxWidth: '100%' } },
                        dropdownButton: { className: 'bg-primary' }
                    }}
                    style={{ height: '30px', width: '100%' }}
                    inputStyle={
                        isMediumMobile
                            ? { fontSize: '13px', padding: '3px', minWidth: 0 }
                            : { minWidth: 0 }
                    }
                    value={lastDate}
                    onChange={(e) => {
                        setLastDate(e.value);
                        fetchChartData(initDate, e.value, { includeTimeline: false });
                    }}
                    showIcon
                    dateFormat="dd/mm/yy"
                    readOnlyInput
                />
            </div>
            <div className="flex-shrink-0 align-self-center">
                <FontAwesomeIcon
                    icon={faDownload}
                    className="bg-primary"
                    style={{
                        color: 'white',
                        width: '14px',
                        height: '14px',
                        borderRadius: '7px',
                        padding: '10px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        cursor: 'pointer',
                        display: 'block'
                    }}
                    title={t('field.download_data')}
                    onClick={() => downloadData(activeMode)}
                />
            </div>
        </div>
    );

    const renderChart = () =>
        isLoading ? (
            <div
                className="w-full flex-shrink-0"
                style={{
                    position: 'relative',
                    height: FIELD_CHART_MODAL_HEIGHT_PX,
                    minHeight: FIELD_CHART_MODAL_HEIGHT_PX,
                    maxHeight: FIELD_CHART_MODAL_HEIGHT_PX,
                    width: '100%'
                }}
            >
                <div
                    className="flex justify-content-center"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }}
                >
                    <div className="loading mt-0" />
                </div>
                <Chart
                    className="w-full"
                    style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, width: '100%', height: '100%' }}
                    type="line"
                />
            </div>
        ) : (
            <div
                className="w-full flex-shrink-0"
                style={{
                    position: 'relative',
                    height: FIELD_CHART_MODAL_HEIGHT_PX,
                    minHeight: FIELD_CHART_MODAL_HEIGHT_PX,
                    maxHeight: FIELD_CHART_MODAL_HEIGHT_PX,
                    width: '100%'
                }}
            >
                <Chart
                    className="w-full"
                    style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, width: '100%', height: '100%' }}
                    type="line"
                    data={chartData}
                    options={chartOptions}
                />
            </div>
        );

    const renderTimeline = () => {
        const showTimelineSpinner = timelineLoading || (isLoading && events.length === 0);
        return (
        <div className="field-chart-modal-timeline-scroll w-full">
            {showTimelineSpinner ? (
                <div
                    className="flex align-items-center justify-content-center w-full"
                    style={{ minHeight: 'min(40dvh, 320px)' }}
                    aria-busy="true"
                >
                    <div className="loading mt-0" />
                </div>
            ) : events.length > 0 ? (
                <Timeline
                    value={events}
                    className="chart-modal-timeline"
                    marker={(item) => (
                        <span
                            className="flex w-1rem h-1rem align-items-center justify-content-center text-white border-circle z-1 shadow-1"
                            style={{ backgroundColor: getChartModalTimelineMarkerColor(item.status) }}
                        />
                    )}
                    content={(item) => (
                        <div className="flex justify-content-between align-items-center w-full">
                            <div className="flex flex-column align-items-start">
                                <small className="text-color-secondary">
                                    {new Date(item.date).toLocaleDateString('es-ES', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    })}
                                </small>
                                <small className="text-color-secondary font-bold">
                                    {isNematode && item.generation !== undefined
                                        ? item.generation === 0
                                            ? `${t('field_card.generation').charAt(0).toUpperCase() + t('field_card.generation').slice(1)} ${t('field_card.initial')}: ${t(`notifications.${item.status}`)}`
                                            : `${item.generation}º ${t('field_card.generation')}: ${t(`notifications.${item.status}`)}`
                                        : t(`notifications.${item.status}`)}
                                </small>
                            </div>
                        </div>
                    )}
                />
            ) : (
                <p className="text-color-secondary text-sm m-0 py-2">{t('field.chart_modal_timeline_empty')}</p>
            )}
        </div>
        );
    };

    return (
        <>
            <ConfirmDialog
                className="w-11 desktop-max-width border-round-2xl"
                visible={showDownloadDataDialog}
                message={t('field.download_data_message')}
                header={t('field.download_data')}
                acceptLabel={t('field.model')}
                rejectLabel={t('field.temperature')}
                acceptClassName={isTemperature ? 'hidden' : 'bg-primary'}
                rejectClassName="bg-primary "
                defaultFocus="none"
                focusOnShow={false}
                onHide={() => setShowDownloadDataDialog(false)}
                accept={async () => {
                    await downloadModelData();
                    setShowDownloadDataDialog(false);
                }}
                reject={async () => {
                    await downloadTemperatureData();
                    setShowDownloadDataDialog(false);
                }}
                pt={{
                    header: { className: 'border-round-top-2xl' },
                    footer: { className: 'border-round-bottom-2xl' }
                }}
            />

            <Dialog
                header={
                    <div className="flex flex-column gap-1">
                        {titleFieldName ? (
                            <span
                                className="text-sm font-medium block"
                                style={{ color: '#757575', lineHeight: 1.25 }}
                            >
                                {titleFieldName}
                            </span>
                        ) : null}
                        <div className="flex align-items-center gap-1 flex-nowrap">
                            <span
                                className="text-color-secondary font-semibold text-base white-space-nowrap"
                                style={{ lineHeight: 1.25 }}
                            >
                                {headerText}
                            </span>
                            {modelSwitcherOptions.length > 1 ? (
                                <>
                                    <Button
                                        type="button"
                                        icon="pi pi-angle-down"
                                        className="p-button-text p-button-rounded shrink-0 text-500 hover:text-700 hover:surface-100 transition-colors transition-duration-150"
                                        style={{
                                            width: '1.625rem',
                                            height: '1.625rem',
                                            minWidth: '1.625rem',
                                            padding: 0,
                                            fontSize: '0.7rem'
                                        }}
                                        title={t('field.chart_modal_switch_model')}
                                        aria-label={t('field.chart_modal_switch_model')}
                                        onClick={(e) => modelMenuRef.current?.toggle(e)}
                                    />
                                    <Menu
                                        ref={modelMenuRef}
                                        model={modelMenuItems}
                                        popup
                                        className="field-chart-model-switch-menu"
                                        pt={{
                                            root: { className: 'field-chart-model-switch-menu' },
                                            menu: { className: 'field-chart-model-switch-menu-list' },
                                            label: { style: { whiteSpace: 'nowrap' } },
                                            action: { style: { whiteSpace: 'nowrap' } }
                                        }}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                }
                visible={visible}
                onHide={handleClose}
                maximized
                draggable={false}
                resizable={false}
                maskClassName="field-chart-fullscreen-mask"
                className="p-2 m-0 w-full h-full border-round-2xl"
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: '100dvh',
                    minHeight: '100dvh',
                    maxHeight: '100dvh',
                    margin: 0,
                    borderRadius: '1rem',
                    border: 'none',
                    boxShadow: 'none'
                }}
                focusOnShow={false}
                pt={{
                    root: {
                        className: 'border-round-2xl overflow-hidden',
                        style: {
                            borderRadius: '1rem',
                            maxWidth: '100vw',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                            border: 'none',
                            boxShadow: 'none',
                            outline: 'none',
                            overflow: 'hidden'
                        }
                    },
                    header: {
                        className: 'border-round-top-2xl',
                        style: {
                            borderRadius: 0,
                            flexShrink: 0,
                            borderBottom: 'none',
                            border: 'none',
                            boxShadow: 'none'
                        }
                    },
                    content: {
                        className: 'p-0 border-round-bottom-2xl',
                        style: {
                            borderRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1 1 0%',
                            minHeight: 0,
                            height: '100%',
                            overflow: 'hidden',
                            border: 'none',
                            boxShadow: 'none'
                        }
                    }
                }}
            >
                {!f ? (
                    <div
                        className="flex align-items-center justify-content-center w-full flex-1"
                        style={{ minHeight: '50dvh' }}
                    >
                        <div className="loading mt-0" />
                    </div>
                ) : (
                    <div
                        className="flex flex-column align-items-stretch w-full flex-1 min-h-0 px-2 sm:px-3 pb-3"
                        style={{
                            flex: '1 1 0%',
                            minHeight: 0,
                            height: '100%',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch'
                        }}
                    >
                        {isTemperature ? (
                            <div className="w-full flex-shrink-0 flex flex-column">
                                {renderCalendars()}
                                {renderChart()}
                            </div>
                        ) : (
                            <TabView className="w-full" panelContainerClassName="w-full">
                                <TabPanel header={t('field.chart_modal_tab_chart')}>
                                    <div className="w-full flex-shrink-0 flex flex-column">
                                        {renderCalendars()}
                                        <div className="w-full flex-shrink-0 flex flex-column pt-1">
                                            {renderChart()}
                                        </div>
                                    </div>
                                </TabPanel>
                                <TabPanel header={t('field.chart_modal_tab_timeline')}>
                                    {renderTimeline()}
                                </TabPanel>
                            </TabView>
                        )}
                    </div>
                )}
            </Dialog>
        </>
    );
};
